import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
	Drawer,
	Box,
	Typography,
	TextField,
	FormControl,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	FormHelperText,
} from '@mui/material';

import { CancelButton } from 'src/components/common/buttons/CancelButton';
import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	ICreateEditPatientModalProps,
	IPatientCreateEditFormData,
} from 'src/features/patient/domain/types';
import { CreateEditPatientValidationSchema } from 'src/features/patient/domain/validation';
import {
	createPatient,
	PATIENT_LOADING_KEY,
	updatePatient,
} from 'src/features/patient/store';
import { AppDispatch } from 'src/store';

export const CreateEditPatientDrawer: React.FC<
	ICreateEditPatientModalProps
> = ({ open, onClose, onSubmit, initialPatientData }) => {
	const { siteSlug } = useParams<{
		siteSlug: string;
	}>();
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[PATIENT_LOADING_KEY],
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<IPatientCreateEditFormData>({
		resolver: yupResolver(CreateEditPatientValidationSchema),
	});

	const handlePatientCreation = async (
		newPatientData: IPatientCreateEditFormData,
	) => {
		if (!siteSlug) return;

		if (initialPatientData) {
			const initialFormData: IPatientCreateEditFormData = {
				first_name: initialPatientData.first_name,
				last_name: initialPatientData.last_name,
				age: initialPatientData.age,
				gender: initialPatientData.gender,
				contact_number: initialPatientData.contact_number,
				address: initialPatientData.address,
				occupation: initialPatientData.occupation,
			};

			if (
				JSON.stringify(newPatientData) ===
				JSON.stringify(initialFormData)
			) {
				onClose?.();
				return;
			}

			dispatch(
				updatePatient({
					siteSlug: siteSlug,
					patientId: initialPatientData.id,
					request: newPatientData,
					onSuccess: () => {
						onSubmit?.(newPatientData);
						snackbar('Patient updated successfully', 'success');
					},
				}),
			);
		} else {
			dispatch(
				createPatient({
					siteSlug: siteSlug,
					request: newPatientData,
					onSuccess: () => {
						onSubmit?.(newPatientData);
						snackbar('Patient created successfully', 'success');
					},
				}),
			);
		}
	};

	useEffect(() => {
		if (open) {
			reset({
				first_name: initialPatientData?.first_name,
				last_name: initialPatientData?.last_name,
				age: initialPatientData?.age,
				gender: initialPatientData?.gender,
				contact_number: initialPatientData?.contact_number ?? null,
				address: initialPatientData?.address ?? null,
				occupation: initialPatientData?.occupation ?? null,
			});
		}
	}, [open, initialPatientData, reset]);

	if (!open) return null;

	return (
		<Drawer
			anchor='right'
			open={open}
			onClose={onClose}
			sx={{
				'& .MuiDrawer-paper': {
					width: '40%',
					boxSizing: 'border-box',
					padding: '32px',
					'@media (max-width: 600px)': {
						width: '100%',
					},
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 3,
				}}
			>
				<Typography variant='h6' component='h2'>
					{initialPatientData ? 'Edit patient' : 'Create New Patient'}
				</Typography>
			</Box>

			<form onSubmit={handleSubmit(handlePatientCreation)}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Controller
						name='first_name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='First Name'
								variant='outlined'
								fullWidth
								error={!!errors.first_name}
								helperText={errors.first_name?.message}
							/>
						)}
					/>

					<Controller
						name='last_name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Last Name'
								variant='outlined'
								fullWidth
								error={!!errors.last_name}
								helperText={errors.last_name?.message}
							/>
						)}
					/>

					<Controller
						name='age'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Age'
								variant='outlined'
								fullWidth
								type='number'
								inputProps={{ min: 1, max: 120 }}
								error={!!errors.age}
								helperText={errors.age?.message}
								onChange={(e) =>
									field.onChange(Number(e.target.value))
								}
							/>
						)}
					/>

					<Controller
						name='gender'
						control={control}
						render={({ field }) => (
							<FormControl error={!!errors.gender}>
								<FormLabel component='legend'>Gender</FormLabel>
								<RadioGroup {...field} row sx={{ mt: 1 }}>
									<FormControlLabel
										value='male'
										control={<Radio />}
										label='Male'
									/>
									<FormControlLabel
										value='female'
										control={<Radio />}
										label='Female'
									/>
									<FormControlLabel
										value='other'
										control={<Radio />}
										label='Other'
									/>
								</RadioGroup>
								{errors.gender && (
									<FormHelperText>
										{errors.gender.message}
									</FormHelperText>
								)}
							</FormControl>
						)}
					/>

					<Controller
						name='contact_number'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Contact Number'
								variant='outlined'
								fullWidth
								error={!!errors.contact_number}
								helperText={errors.contact_number?.message}
							/>
						)}
					/>

					<Controller
						name='address'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Address'
								variant='outlined'
								fullWidth
								error={!!errors.address}
								helperText={errors.address?.message}
							/>
						)}
					/>

					<Controller
						name='occupation'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Occupation'
								variant='outlined'
								fullWidth
								error={!!errors.occupation}
								helperText={errors.occupation?.message}
							/>
						)}
					/>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							mt: 2,
						}}
					>
						<PrimaryButton type='submit' isLoading={isLoading}>
							{initialPatientData ? 'SAVE' : 'CREATE'}
						</PrimaryButton>
						<CancelButton onClick={onClose}>Cancel</CancelButton>
					</Box>
				</Box>
			</form>
		</Drawer>
	);
};
